import '@banno/jha-wc/src/buttons/jha-button/jha-button';
import { exists } from '@jack-henry/frontend-utils/functions';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { type Action } from '@jack-henry/frontend-utils/types';
import '@jack-henry/platform-tools/components/jh-platform-drawer';
// eslint-disable-next-line no-duplicate-imports
import type JhPlatformDrawer from '@jack-henry/platform-tools/components/jh-platform-drawer';
import { noOp } from '@treasury/utils/functions';
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import {
    getContentFromSlot,
    isProviderHost,
    listenForDrawerResult,
    SideDrawerButton,
    SideDrawerButtonPosture,
    SideDrawerProvider,
} from './side-drawer.service.types';
// eslint-disable-next-line no-duplicate-imports
import '@banno/jha-wc/src/buttons/jha-button/jha-button';
// eslint-disable-next-line no-duplicate-imports
import JhaButton from '@banno/jha-wc/src/buttons/jha-button/jha-button';

export const tagName = 'tm-side-drawer';

@customElement(tagName)
export class TmSideDrawer extends LitElement {
    @property()
    public heading = '';

    @property()
    public readonly alignment: 'right' | 'left' = 'right';

    @property()
    public readonly footerAlignment: 'right' | 'left' = 'left';

    @property({
        attribute: false,
        reflect: false,
    })
    public buttons: SideDrawerButton[] = [];

    public get closeComplete() {
        const drawer = this.drawerRef.value;
        if (!drawer) {
            throw new Error('Could not determine closed state');
        }

        const animationPromises = drawer.getAnimations().map((a: Animation) => a.finished);

        return Promise.all(animationPromises).then(noOp);
    }

    @state()
    private closed = false;

    @state()
    private data?: unknown;

    private readonly drawerRef = createRef<JhPlatformDrawer>();

    private readonly slotRef = createRef<HTMLSlotElement>();

    private readonly buttonContainerRef = createRef<Element>();

    private readonly disconnectedTasks: Action[] = [];

    public firstUpdated() {
        this.listenForResult();
    }

    public disconnectedCallback() {
        this.disconnectedTasks.forEach(task => task());
    }

    /**
     * Close the drawer programmatically from an external source.
     */
    public close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        this.closed = true;
        data = data || (this.data as T);

        const result = {
            data,
            reason,
        };

        const closeEvent = new CustomEvent('close-drawer', {
            detail: result,
        });
        this.dispatchEvent(closeEvent);
    }

    public getButtons() {
        const { value: buttonContainer } = this.buttonContainerRef;
        if (!buttonContainer) {
            throw new Error('Could not get side drawer buttons');
        }

        return Array.from(buttonContainer.querySelectorAll<JhaButton>('jha-button'));
    }

    public render() {
        const conditionalClasses = classMap({
            [`slide-in-${this.alignment}`]: !this.closed,
            [`slide-out-${this.alignment}`]: this.closed,
        });

        return html`<jh-platform-drawer
            ${ref(this.drawerRef)}
            class="${conditionalClasses} max-w-full"
            .heading=${this.heading}
            @close-drawer=${() => {
                this.closed = true;
            }}
        >
            <slot ${ref(this.slotRef)} @slotchange=${() => this.onSlotChange()}></slot>
            <div
                slot="footer"
                id="footer"
                class=${classMap({
                    'flex flex-row': true,
                    'justify-end': this.footerAlignment === 'right',
                    'justify-start': this.footerAlignment === 'left',
                })}
            >
                ${this.renderButtons()}
            </div>
        </jh-platform-drawer>`;
    }

    private listenForResult() {
        const drawerElement = this.drawerRef.value;
        if (!drawerElement) {
            throw new Error('Could not listen for drawer result; element is not available.');
        }

        const { unsubscribe } = listenForDrawerResult(drawerElement, result => {
            this.data = result;
        });

        this.disconnectedTasks.push(unsubscribe);
    }

    private onSlotChange() {
        const { value: slot } = this.slotRef;

        if (!slot) {
            return;
        }

        const content = getContentFromSlot(slot);
        if (exists(content) && isProviderHost(content)) {
            this.hydrateFromContentProvider(content.provider);
        }
    }

    private hydrateFromContentProvider(provider: SideDrawerProvider) {
        const { drawerButtons } = provider;

        this.buttons = drawerButtons || this.buttons;
    }

    private renderButtons() {
        const buttonTemplates = this.buttons.map(b => {
            const { onClick, checkEnabled, reason, label } = b;
            const isEnabled = checkEnabled ? checkEnabled(this.data) : true;
            // if no click handler is provided, buttons will close the drawer by default
            const clickHandler = onClick || (() => this.close(reason));

            return html`<jha-button
                ?primary=${b.posture === SideDrawerButtonPosture.Primary}
                ?secondary=${b.posture === SideDrawerButtonPosture.Secondary}
                ?tertiary=${b.posture === SideDrawerButtonPosture.Tertiary}
                ?success=${b.posture === SideDrawerButtonPosture.Constructive}
                ?danger=${b.posture === SideDrawerButtonPosture.Destructive}
                class="drawer-button"
                ?disabled=${!isEnabled}
                @click=${() => {
                    clickHandler(
                        data => this.close(reason, data),
                        data => (this.data = data)
                    );
                }}
                >${label}</jha-button
            >`;
        });

        // place button templates in a container div so that they can be queried easily
        return html`<div ${ref(this.buttonContainerRef)}>${buttonTemplates}</div>`;
    }

    public static get styles() {
        return css`
            :host {
                display: flex;
                height: 100%;
                max-width: 100%;
            }
            #footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
            }
            .drawer-button {
                padding: 4px;
            }
            .slide-in-right {
                animation: slide-in-right 0.5s;
            }

            .slide-out-right {
                animation: slide-out-right 1s forwards;
            }

            .slide-in-left {
                animation: slide-in-left 0.5s;
            }

            .slide-out-left {
                animation: slide-out-left 1s forwards;
            }

            @keyframes slide-in-left {
                from {
                    translate: -150vw 0;
                    display: none;
                }

                to {
                    translate: 0 0;
                }
            }

            @keyframes slide-out-left {
                from {
                    translate: 0 0;
                }

                to {
                    translate: -150vw 0;
                    display: none;
                }
            }

            @keyframes slide-in-right {
                from {
                    translate: 150vw 0;
                    display: none;
                }

                to {
                    translate: 0 0;
                }
            }

            @keyframes slide-out-right {
                from {
                    translate: 0 0;
                }

                to {
                    translate: 150vw 0;
                    display: none;
                }
            }
        `;
    }
}
